import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '../icon/Icon';
import { Spinner } from '../spinner/Spinner';
import { BUTTON_ICON_SIZE, buttonVariants, type ButtonVariantProps } from './Button.variants';

export interface ButtonProps
  extends
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    Omit<ButtonVariantProps, 'size'> {
  children?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Design-system icon rendered alongside the label. */
  icon?: IconName;
  iconPosition?: 'start' | 'end';
  /**
   * Replaces the icon with a spinner and blocks interaction. Bet placement and
   * wallet actions are always in flight long enough to need this.
   */
  loading?: boolean;
}

export function Button({
  children,
  className,
  variant,
  size = 'md',
  fullWidth,
  icon,
  iconPosition = 'start',
  loading = false,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const iconSize = BUTTON_ICON_SIZE[size];
  const glyph = loading ? (
    <Spinner size={size === 'lg' ? 'md' : 'sm'} className="text-current" />
  ) : icon ? (
    <Icon name={icon} size={iconSize} />
  ) : null;

  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {iconPosition === 'start' && glyph}
      {children}
      {iconPosition === 'end' && glyph}
    </button>
  );
}
